<template>
    <v-container fluid class="supplier-docs-page pa-6">

        <!-- Resync -->
        <div class="d-flex justify-end mb-4">
            <v-btn
                color="#525A68" rounded dark elevation="0"
                @click="resync_dialog = true"
            >
                <v-icon left small>mdi-sync</v-icon>
                Resync Supplier Documents
            </v-btn>
        </div>

        <!-- Upload custom document -->
        <v-card class="upload-card mb-8" elevation="0">
            <v-card-text class="pa-4">
                <v-row align="center">
                    <v-col cols="12" sm="5">
                        <v-file-input
                            v-model="doc_file"
                            label="Select file"
                            outlined dense hide-details="auto"
                            prepend-inner-icon="mdi-upload"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="doc_name"
                            label="Document name (optional)"
                            outlined dense hide-details="auto"
                            prepend-inner-icon="mdi-tag-outline"
                        />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-btn
                            color="primary" rounded elevation="0"
                            :loading="doc_uploading"
                            :disabled="!doc_file"
                            @click="uploadDocument()"
                        >
                            <v-icon left small>mdi-upload</v-icon>
                            Upload
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Loading state -->
        <div v-if="loading" class="doc-empty-state">
            <v-progress-circular indeterminate color="primary" size="40" class="mb-3" />
            <div class="grey--text text--darken-1 body-2">Loading documents...</div>
        </div>

        <!-- Empty state -->
        <div v-else-if="groups.length === 0" class="doc-empty-state">
            <v-icon size="60" color="#E0E0E0" class="mb-3">mdi-file-outline</v-icon>
            <div class="grey--text text--darken-1 body-2">No supplier documents available</div>
        </div>

        <!-- Grouped -->
        <div v-else>
            <div v-for="group in groups" :key="group.key" class="supplier-group mb-8">
                <div class="group-header mb-4">
                    <v-icon small class="mr-2" color="#525A68">{{ group.icon }}</v-icon>
                    {{ group.label }}
                </div>

                <v-row>
                    <v-col
                        v-for="doc in group.documents"
                        :key="doc.id"
                        cols="12" sm="6" md="3"
                    >
                        <v-card class="doc-card" :class="{ 'doc-card--hidden': doc.is_hidden }" elevation="0">
                            <!-- Thumbnail -->
                            <a :href="docUrl(doc)" target="_blank" class="doc-thumb-link">
                                <div class="doc-thumb">
                                    <img
                                        v-if="doc.mime_type && doc.mime_type.startsWith('image/')"
                                        :src="docUrl(doc)"
                                        class="doc-img"
                                    />
                                    <div v-else class="doc-icon-wrap">
                                        <v-icon size="48" :color="docIconColor(doc.mime_type)">{{ docIcon(doc.mime_type) }}</v-icon>
                                    </div>
                                </div>
                            </a>

                            <v-card-text class="doc-body">
                                <div class="doc-name-row">
                                    <div class="doc-name" :title="doc.name">{{ doc.name }}</div>
                                    <v-menu offset-y left>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop>
                                                <v-icon size="18" color="#9E9E9E">mdi-dots-vertical</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <v-list-item :href="docUrl(doc)" target="_blank">
                                                <v-list-item-icon class="mr-2"><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                                                <v-list-item-title>View</v-list-item-title>
                                            </v-list-item>
                                            <v-list-item @click="openRenameDialog(doc)">
                                                <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                                                <v-list-item-title>Rename</v-list-item-title>
                                            </v-list-item>
                                            <v-list-item @click="toggleHidden(doc)">
                                                <v-list-item-icon class="mr-2"><v-icon small>{{ doc.is_hidden ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon></v-list-item-icon>
                                                <v-list-item-title>{{ doc.is_hidden ? 'Unhide' : 'Hide' }}</v-list-item-title>
                                            </v-list-item>
                                            <v-list-item @click="openDeleteDialog(doc)">
                                                <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                                                <v-list-item-title class="error--text">Delete</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>
                                <div class="doc-date grey--text d-flex align-center">
                                    <span>{{ doc.created_at ? doc.created_at.substring(0, 10) : '' }}</span>
                                    <v-chip v-if="doc.is_hidden" x-small class="ml-2" color="#F5F5F5" text-color="#9E9E9E">Hidden</v-chip>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Resync confirm dialog -->
        <v-dialog v-model="resync_dialog" max-width="460">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Resync Supplier Documents</v-card-title>
                <v-card-text>
                    This will remove all documents on this event that came from a supplier or product, and re-add the
                    current supplier and product documents for the suppliers booked on this event.
                    <br><br>
                    Use this if you've uploaded new supplier/product documents that haven't synced yet, or if one was
                    accidentally deleted from the event. Any renaming or hiding you've done to synced documents will
                    be reset, and any custom documents you've uploaded directly to this event will not be affected.
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="resync_dialog = false">Cancel</v-btn>
                    <v-btn color="primary" elevation="0" rounded :loading="resyncing" @click="confirmResync()">Resync</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Rename dialog -->
        <v-dialog v-model="rename_dialog" max-width="420">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Rename Document</v-card-title>
                <v-card-text>
                    <v-text-field v-model="rename_value" label="Document name" outlined dense autofocus />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="rename_dialog = false">Cancel</v-btn>
                    <v-btn color="primary" elevation="0" rounded @click="confirmRename()">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete confirm dialog -->
        <v-dialog v-model="delete_dialog" max-width="380">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Delete Document</v-card-title>
                <v-card-text>Are you sure you want to delete <strong>{{ delete_doc && delete_doc.name }}</strong>? This cannot be undone.</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="delete_dialog = false">Cancel</v-btn>
                    <v-btn color="error" elevation="0" rounded @click="confirmDelete()">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    data: () => ({
        api_url: null,
        loading: true,
        documents: [],
        doc_file: null,
        doc_name: '',
        doc_uploading: false,
        rename_dialog: false,
        rename_doc: null,
        rename_value: '',
        delete_dialog: false,
        delete_doc: null,
        resync_dialog: false,
        resyncing: false,
    }),
    async mounted() {
        this.api_url = process.env.VUE_APP_API_URL
        await this.fetchDocuments()
    },
    computed: {
        ...mapGetters({
            event: 'eventsStore/getSelectedEventGetter',
        }),
        groups() {
            const supplier_groups = new Map()
            const custom_documents = []

            this.documents.forEach(doc => {
                if (doc.is_custom || !doc.supplier) {
                    custom_documents.push(doc)
                    return
                }
                if (!supplier_groups.has(doc.supplier_id)) {
                    supplier_groups.set(doc.supplier_id, {
                        key: `supplier-${doc.supplier_id}`,
                        label: doc.supplier.supplier_name,
                        icon: 'mdi-store-outline',
                        supplier_documents: [],
                        product_documents: [],
                    })
                }
                const group = supplier_groups.get(doc.supplier_id)
                if (doc.supplier_product_document_id) {
                    group.product_documents.push(doc)
                } else {
                    group.supplier_documents.push(doc)
                }
            })

            const groups = Array.from(supplier_groups.values()).map(group => ({
                key: group.key,
                label: group.label,
                icon: group.icon,
                documents: [...group.supplier_documents, ...group.product_documents],
            }))
            if (custom_documents.length > 0) {
                groups.push({
                    key: 'custom',
                    label: 'Custom Documents',
                    icon: 'mdi-paperclip',
                    documents: custom_documents,
                })
            }
            return groups
        },
    },
    methods: {
        docUrl(doc) {
            let folder = 'suppliers/documents'
            if (doc.is_custom) folder = 'events/documents'
            else if (doc.supplier_product_document_id) folder = 'suppliers/product_documents'
            return `${this.api_url}media/${folder}/${doc.file_name}`
        },
        async fetchDocuments() {
            this.loading = true
            try {
                const { data } = await this.$axios.get(`admin/events/get_event_supplier_documents/${this.event.id}`)
                if (data.response) {
                    this.documents = data.data
                }
            } finally {
                this.loading = false
            }
        },
        async confirmResync() {
            this.resyncing = true
            try {
                const { data } = await this.$axios.post(`admin/events/resync_event_supplier_documents/${this.event.id}`)
                if (data.response) {
                    this.$toast.success(data.message)
                    this.documents = data.data
                }
            } finally {
                this.resyncing = false
                this.resync_dialog = false
            }
        },
        async uploadDocument() {
            if (!this.doc_file) return
            this.doc_uploading = true
            try {
                const formData = new FormData()
                formData.append('document', this.doc_file)
                formData.append('name', this.doc_name || this.doc_file.name)
                const { data } = await this.$axios.post(`admin/events/upload_event_supplier_document/${this.event.id}`, formData)
                if (data.response) {
                    this.$toast.success(data.message)
                    this.doc_file = null
                    this.doc_name = ''
                    this.documents.push(data.data)
                }
            } finally {
                this.doc_uploading = false
            }
        },
        openRenameDialog(doc) {
            this.rename_doc = doc
            this.rename_value = doc.name
            this.rename_dialog = true
        },
        async confirmRename() {
            if (!this.rename_doc || !this.rename_value.trim()) return
            const { data } = await this.$axios.post(`admin/events/rename_event_supplier_document/${this.rename_doc.id}`, { name: this.rename_value })
            if (data.response) {
                this.$toast.success(data.message)
                const doc = this.documents.find(d => d.id === this.rename_doc.id)
                if (doc) doc.name = this.rename_value
            }
            this.rename_dialog = false
            this.rename_doc = null
        },
        async toggleHidden(doc) {
            const { data } = await this.$axios.post(`admin/events/hide_event_supplier_document/${doc.id}`, { is_hidden: !doc.is_hidden })
            if (data.response) {
                this.$toast.success(data.message)
                doc.is_hidden = !doc.is_hidden
            }
        },
        openDeleteDialog(doc) {
            this.delete_doc = doc
            this.delete_dialog = true
        },
        async confirmDelete() {
            if (!this.delete_doc) return
            const { data } = await this.$axios.delete(`admin/events/delete_event_supplier_document/${this.delete_doc.id}`)
            if (data.response) {
                this.$toast.success(data.message)
                this.documents = this.documents.filter(d => d.id !== this.delete_doc.id)
            }
            this.delete_dialog = false
            this.delete_doc = null
        },
        docIcon(mime) {
            if (!mime) return 'mdi-file-outline'
            if (mime === 'application/pdf') return 'mdi-file-pdf-box'
            if (mime.includes('word') || mime.includes('docx') || mime.includes('doc')) return 'mdi-file-word-box'
            if (mime.includes('excel') || mime.includes('xlsx') || mime.includes('xls') || mime.includes('spreadsheet')) return 'mdi-file-excel-box'
            if (mime.includes('powerpoint') || mime.includes('pptx') || mime.includes('ppt') || mime.includes('presentation')) return 'mdi-file-powerpoint-box'
            if (mime.startsWith('text/')) return 'mdi-file-document-outline'
            if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return 'mdi-folder-zip-outline'
            return 'mdi-file-outline'
        },
        docIconColor(mime) {
            if (!mime) return '#9E9E9E'
            if (mime === 'application/pdf') return '#E53935'
            if (mime.includes('word') || mime.includes('docx') || mime.includes('doc')) return '#1565C0'
            if (mime.includes('excel') || mime.includes('xlsx') || mime.includes('xls') || mime.includes('spreadsheet')) return '#2E7D32'
            if (mime.includes('powerpoint') || mime.includes('pptx') || mime.includes('ppt') || mime.includes('presentation')) return '#E65100'
            if (mime.startsWith('text/')) return '#546E7A'
            if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return '#6D4C41'
            return '#9E9E9E'
        },
    },
}
</script>

<style scoped lang="scss">
.supplier-docs-page {
    background-color: #F5F6FA;
    min-height: 100%;
}

.upload-card {
    border-radius: 12px !important;
    border: 1px solid #E8E8E8 !important;
    background-color: #fff;
}

.doc-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    text-align: center;
}

.group-header {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #525A68;
    border-bottom: 2px solid #EBEBEB;
    padding-bottom: 8px;
}

/* Document card — matches SetupComponent style */
.doc-card {
    border-radius: 12px !important;
    border: 1px solid #E8E8E8 !important;
    background-color: #fff;
    overflow: hidden;
    transition: box-shadow 0.15s ease, transform 0.15s ease;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.09) !important;
        transform: translateY(-1px);
    }

    &--hidden {
        opacity: 0.55;
    }
}

.doc-thumb-link {
    display: block;
    text-decoration: none;

    &:hover .doc-thumb {
        opacity: 0.88;
    }
}

.doc-thumb {
    position: relative;
    width: 100%;
    height: 140px;
    background-color: #F5F5F5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.doc-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.doc-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.doc-body {
    padding: 12px 14px 10px !important;
}

.doc-name-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
}

.doc-name {
    font-size: 13px;
    font-weight: 700;
    color: #2C2C2C;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
}

.doc-date {
    font-size: 11px;
    margin-top: 2px;
}
</style>
